import moment from 'moment';
import * as dates from 'date-arithmetic';
import { AvailabilityBlockObject } from '../slices/marketplace/meetings/models/availability-block-object.model';
import { CalendarObject } from '../slices/marketplace/meetings/models/calendar-object.model';
import { EngagementMeetingProposalContract } from '../slices/marketplace/meetings/models/engagement-meeting-proposal-contract';
import { MeetingObject } from '../slices/marketplace/meetings/models/meeting-object.model';
import { EngagementMeeting } from '../slices/marketplace/meetings/models/get-engagement-conference';
import { TimelineParty } from '../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { MeetingProposalStatus } from '../slices/marketplace/meetings/models/enums/meeting-proposal-status.enum';
import { EngagementBuyer } from '../slices/marketplace/timeline/models/engagement-buyer.model';
import { MeetingParticipantsType } from '../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { EngagementMeetingStatus } from '../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { MeetingScheduleType } from '../slices/marketplace/meetings/models/enums/meeting-schedule-type.enum';
import { EngagementMeetingAvailabilityBlockContract } from '../slices/marketplace/meetings/models/engagement-meeting-availability-block-contract';
import { MarketplaceOpportunity } from '../slices/marketplace/opportunity/models/opportunity';
import { TeamMemberType, InternalPermissions, ExternalPermissions } from '../containers/Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';
import { USER_ROLES } from '../utils/constants';
import { MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION } from '../utils/associateMembersConstants';

export function isAvailabilityBlockObject(
    obj?: CalendarObject | null
): obj is AvailabilityBlockObject {
    if (!obj) return false;
    return 'availabilityBlockId' in obj;
}

export const sortEventsByDate = (events: CalendarObject[]) => {
    if (!events || events.length === 0) {
        return [];
    }
    return events.sort((a, b) => moment(a.dateStart).diff(moment(b.dateStart)));
};

export const mapEngagementMeetingToMeetingObject = (
    meeting: EngagementMeeting,
    proposals: EngagementMeetingProposalContract[],
    buyers?: EngagementBuyer[],
    asOfDate?: Date
): MeetingObject => {
    const proposal = proposals.find(
        (p) => p.meetingId === meeting.engagementMeetingId
    );
    const buyerId = proposal?.buyerId;
    const buyer = buyerId != null && buyers?.length ? buyers.find((b) => b.buyerId === buyerId) : undefined;
    const buyerDisplayName = buyer ? (buyer.companyName || buyer.buyerAccountName || '') : '';

    const resolveAddress = () => {
        if (!proposal) {
            const loc = meeting.location;
            const fromLocation =
                loc && (loc.city || loc.state || loc.address);
            if (fromLocation) {
                return {
                    proposedByParty: TimelineParty.Buyer,
                    city: loc.city || '',
                    state: loc.state || '',
                    address: loc.address,
                };
            }
            return meeting.city || meeting.state || meeting.address
                ? {
                      proposedByParty: TimelineParty.Buyer,
                      city: meeting.city || '',
                      state: meeting.state || '',
                      address: meeting.address,
                  }
                : undefined;
        }
        const meetingStart =
            meeting.dateStart instanceof Date
                ? meeting.dateStart
                : new Date(meeting.dateStart);
        const now = asOfDate ?? new Date();
        const useConfirmedOnly = meetingStart <= now;
        return useConfirmedOnly
            ? proposal.confirmedAddress
            : (proposal.alternativeAddress || proposal.confirmedAddress);
    };

    return {
        id: meeting.id,
        dateStart: meeting.dateStart,
        dateEnd: meeting.dateEnd,
        posterId: buyerId ?? meeting.posterId,
        posterName: buyerDisplayName || meeting.posterName,
        buyerId: buyerId,
        name: meeting.name,
        message: meeting.message,
        address: resolveAddress(),
        format: meeting.format,
        participants: meeting.participants,
        files: meeting.files,
        isInProgress: meeting.isInProgress,
        participantsType: meeting.participantsType,
        status: meeting.status,
        ScheduleType: meeting.ScheduleType,
        isRecordingAvailable: meeting.isRecordingAvailable,
        engagementId: meeting.engagementId,
        threadedDiscussionId: meeting.threadedDiscussionId,
        commsId: meeting.commsId,
        engagementMeetingId: meeting.engagementMeetingId,
        alternativeAddress: proposal?.alternativeAddress || undefined,
        confirmedAddress: proposal?.confirmedAddress || undefined,
        proposalStatus: MeetingProposalStatus.Confirmed,
        isMeetingProposal: false,
        isCancelled: proposal?.isCancelled,
        cancellationReason: proposal?.cancellationReason,
        pricePaid: proposal?.pricePaid,
        refundedAmount: proposal?.refundedAmount,
        meetingProposalId: proposal?.id || undefined,
        meetingParticipants:
            proposal?.meetingParticipants ||
            meeting.meetingParticipants,
        confirmedCancellationDeadlineDays: proposal?.confirmedCancellationDeadlineDays ?? meeting.confirmedCancellationDeadlineDays,
        confirmedCancellationDeadlineHours: proposal?.confirmedCancellationDeadlineHours ?? meeting.confirmedCancellationDeadlineHours,
        proposedCancellationDeadlineDays: proposal?.proposedCancellationDeadlineDays ?? meeting.proposedCancellationDeadlineDays,
        proposedCancellationDeadlineHours: proposal?.proposedCancellationDeadlineHours ?? meeting.proposedCancellationDeadlineHours,
        timeZone: meeting.timeZone ?? proposal?.timeZone,
    };
};

export const mapProposalToMeetingObject = (
    proposal: EngagementMeetingProposalContract,
    buyers?: EngagementBuyer[],
    asOfDate?: Date
): MeetingObject => {
    const buyer = buyers?.find((b) => b.buyerId === proposal.buyerId);
    const posterName =
        buyer?.companyName || buyer?.buyerAccountName || '';
    const proposalStart =
        proposal.dateStart instanceof Date
            ? proposal.dateStart
            : new Date(proposal.dateStart);
    const now = asOfDate ?? new Date();
    const useConfirmedOnly = proposalStart <= now;
    const address = useConfirmedOnly
        ? proposal.confirmedAddress
        : (proposal.alternativeAddress || proposal.confirmedAddress);
    return {
        id: 0,
        dateStart: proposal.dateStart,
        dateEnd: proposal.dateEnd,
        posterId: proposal.buyerId,
        posterName,
        buyerId: proposal.buyerId,
        name: '',
        message: proposal.description || '',
        address,
        format: proposal.format,
        participants: [],
        files: [],
        isInProgress: false,
        participantsType: MeetingParticipantsType.SellerBuyer,
        ScheduleType: MeetingScheduleType.Scheduled,
        status: EngagementMeetingStatus.Upcoming,
        proposalStatus: proposal.status,
        isRecordingAvailable: false,
        engagementId: proposal.engagementId,
        threadedDiscussionId: proposal.threadedDiscussionId,
        commsId: proposal.commsId,
        alternativeAddress: proposal.alternativeAddress,
        confirmedAddress: proposal.confirmedAddress,
        isMeetingProposal: true,
        meetingProposalId: proposal.id,
        engagementMeetingId: proposal.meetingId,
        meetingParticipants: proposal.meetingParticipants,
        confirmedCancellationDeadlineDays: proposal.confirmedCancellationDeadlineDays,
        confirmedCancellationDeadlineHours: proposal.confirmedCancellationDeadlineHours,
        proposedCancellationDeadlineDays: proposal.proposedCancellationDeadlineDays,
        proposedCancellationDeadlineHours: proposal.proposedCancellationDeadlineHours,
        isCancelled: proposal.isCancelled,
        cancellationReason: proposal.cancellationReason,
        pricePaid: proposal.pricePaid,
        refundedAmount: proposal.refundedAmount,
        timeZone: proposal.timeZone,
    };
};

export const mapAvailabilityBlockToObject = (
    block: EngagementMeetingAvailabilityBlockContract,
    engagementId?: number
): AvailabilityBlockObject => {
    return {
        id: 0,
        availabilityBlockId: block.id,
        dateStart: new Date(block.dateStart),
        dateEnd: new Date(block.dateEnd),
        posterId: 0,
        posterName: 'Availability Block',
        name: 'Availability Block',
        message: '',
        address:
            block.city || block.state || block.address
                ? {
                      proposedByParty: TimelineParty.Seller,
                      city: block.city || '',
                      state: block.state || '',
                      address: block.address,
                  }
                : undefined,
        formats: block.formats,
        buyers: block.buyers,
        hasCancellationFee: block.hasCancellationFee,
        engagementId: engagementId,
    };
};

export const getOpportunityDeadlineFromEvents = (
    opp: MarketplaceOpportunity
) => {
    const latestEvent = opp.events.reduce((latest, event) => {
        return event.dayEnd > latest.dayEnd ? event : latest;
    });
    return dates.add(opp.listingDate, latestEvent.dayEnd, 'day');
};

export const canBuyerTeamMemberJoinMeeting = (
    summary: any,
    engagementId: number | null | undefined
): boolean => {
    if (summary.roleId !== USER_ROLES.PRACTICE_SECONDARY_USER) {
        return true;
    }

    if (!summary?.associateMember?.length) {
        return false;
    }

    const mappings =
        summary.associateMember[0]?.associateMemberMarketplaceMappings;

    if (!mappings || mappings.length === 0) {
        return false;
    }

    return mappings.some((mapping: any) => {
        if (mapping.isActingPrimaryBuyer) {
            return true;
        }

        const isOpportunityMatch = mapping.opportunityId === engagementId;
        if (!isOpportunityMatch) {
            return false;
        }

        if (mapping.type === TeamMemberType.Internal) {
            return (
                mapping.internalPermissions === InternalPermissions.ReadOnlyAndCommunicate ||
                mapping.internalPermissions === InternalPermissions.ReadOnlyAndCommunicateAndExecute
            );
        }

        if (mapping.type === TeamMemberType.External) {
            return mapping.externalPermissions === ExternalPermissions.VdrAndCommunicate;
        }

        return false;
    });
};

export const canBuyerTeamMemberManageMeetings = (
    summary: any,
    engagementId: number | null | undefined
): boolean => {
    if (summary.roleId !== USER_ROLES.PRACTICE_SECONDARY_USER) {
        return true;
    }

    if (!summary?.associateMember?.length) {
        return false;
    }

    const mappings =
        summary.associateMember[0]?.associateMemberMarketplaceMappings;

    if (!mappings || mappings.length === 0) {
        return false;
    }

    const hasFullAccessMapping = mappings.some((mapping: any) => {
        if (mapping.isActingPrimaryBuyer) {
            return true;
        }

        const isOpportunityMatch = mapping.opportunityId === engagementId;

        return (
            isOpportunityMatch &&
            mapping.type === TeamMemberType.Internal &&
            mapping.internalPermissions ===
                InternalPermissions.ReadOnlyAndCommunicateAndExecute
        );
    });

    return hasFullAccessMapping;
};

export const isSellerTeamMemberForEngagement = (
    summary: any,
    engagementId: number | null | undefined
): boolean => {
    if (summary?.roleId !== USER_ROLES.PRACTICE_SECONDARY_USER) {
        return false;
    }
    if (!summary?.associateMember?.length) {
        return false;
    }
    for (const am of summary.associateMember as any[]) {
        if (!am.associateMemberMarketplaceMappings) continue;
        for (const mapping of am.associateMemberMarketplaceMappings as any[]) {
            const isSellerSide = mapping.isSellerSide ?? mapping.IsSellerSide;
            const mappingEngagementId = mapping.engagementId ?? mapping.EngagementId;
            if (isSellerSide && mappingEngagementId == engagementId) {
                return true;
            }
        }
    }
    return false;
};

export const canSellerTeamMemberManageMeetings = (
    summary: any,
    engagementId: number | null | undefined
): boolean => {
    if (summary.roleId !== USER_ROLES.PRACTICE_SECONDARY_USER) {
        return true;
    }

    if (!summary?.associateMember?.length) {
        return false;
    }

    for (const am of summary.associateMember as any[]) {
        if (!am.associateMemberMarketplaceMappings) continue;
        for (const mapping of am.associateMemberMarketplaceMappings as any[]) {
            const isSellerSide = mapping.isSellerSide ?? mapping.IsSellerSide;
            const mappingEngagementId = mapping.engagementId ?? mapping.EngagementId;
            if (
                isSellerSide &&
                mappingEngagementId == engagementId &&
                mapping.currentSellerTMPermissions?.length > 0
            ) {
                return (mapping.currentSellerTMPermissions as any[])
                    .map(Number)
                    .includes(MARKETPLACE_SELLER_TEAM_MEMBER_PERMISSION.CommsHub);
            }
        }
    }
    return false;
};

export interface MonthNavigationMeetingCounts {
    prev: number;
    next: number;
}

// Persistent month-view nav badges (#945): unlike notification flags these are
// client-computed and never cleared on viewing. `prev` totals every qualifying
// meeting before the displayed month and `next` every one after it — not just
// the adjacent months — so the badges point at meetings any number of months
// away. A meeting belongs to the month of its start date — the same
// attribution the calendar day highlights use.
export const getMonthNavigationMeetingCounts = (
    calendarObjects: CalendarObject[] | null | undefined,
    displayedMonth: Date,
    now: Date = new Date()
): MonthNavigationMeetingCounts => {
    const qualifying = (calendarObjects || []).filter((obj) => {
        if (isAvailabilityBlockObject(obj)) {
            return false;
        }
        const meeting = obj as MeetingObject;
        if (meeting.isCancelled) {
            return false;
        }
        const isUpcomingOrInProgress =
            meeting.status === EngagementMeetingStatus.Upcoming ||
            meeting.status === EngagementMeetingStatus.InProgress;
        if (!isUpcomingOrInProgress) {
            return false;
        }
        // Statuses are flipped by background jobs, so guard against
        // already-past meetings the jobs have not transitioned yet.
        const end = meeting.dateEnd || meeting.dateStart;
        return !!end && !moment(end).isBefore(moment(now));
    });

    const displayedStart = moment(displayedMonth).startOf('month');
    const displayedEnd = moment(displayedMonth).endOf('month');

    return {
        prev: qualifying.filter((obj) =>
            moment(obj.dateStart).isBefore(displayedStart)
        ).length,
        next: qualifying.filter((obj) =>
            moment(obj.dateStart).isAfter(displayedEnd)
        ).length,
    };
};
